import { createContext, useContext, useState, useCallback, useRef, useEffect, ReactNode, useMemo } from 'react'
import {
  fetchScreenDisplayDetail,
  ScreenDisplayDetailData,
  ScreenDetail,
  Assignment,
  EnterpriseInfo,
} from 'src/admin/core/services/screens'
import { getAppBarState } from 'src/store'

interface ScreensContextValue {
  /** Whether there are any pending changes across all tabs */
  hasChanges: boolean
  /** Whether there are validation errors preventing save */
  hasValidationErrors: boolean
  /** Whether a save operation is in progress */
  saving: boolean
  /** Whether the shared data is still loading */
  loadingSharedData: boolean
  /** Error from shared data loading, if any */
  sharedDataError: string | null

  // ─── Shared data from screen-administration-data endpoint ───
  /** Screens list (sorted by name) */
  screens: ScreenDetail[]
  /** Available assignments (sorted by order) */
  assignments: Assignment[]
  /** Enterprise info (schedule, base_url, etc.) */
  enterprise: EnterpriseInfo | null

  /** Reload the shared data from the API */
  reloadSharedData: () => Promise<void>

  /** Report whether a specific tab has unsaved changes */
  setTabDirty: (tabKey: string, isDirty: boolean) => void
  /** Check if a specific tab is dirty */
  isTabDirty: (tabKey: string) => boolean
  /** Set whether there are validation errors */
  setHasValidationErrors: (hasErrors: boolean) => void
  /** Register a save handler for a specific tab */
  registerSaveHandler: (tabKey: string, handler: (() => Promise<void>) | null) => void
  /** Trigger save for all dirty tabs (called from header button) */
  onSave: () => Promise<void>

  /** Update screens list in shared state (e.g. after upsert) */
  setScreens: (screens: ScreenDetail[]) => void
  /** Update assignments list in shared state (e.g. after sync) */
  setAssignments: (updater: Assignment[] | ((prev: Assignment[]) => Assignment[])) => void
}

const ScreensContext = createContext<ScreensContextValue | null>(null)

export const ScreensProvider = ({ children }: { children: ReactNode }) => {
  const [dirtyTabs, setDirtyTabs] = useState<Record<string, boolean>>({})
  const [saving, setSaving] = useState(false)
  const [hasValidationErrors, setHasValidationErrorsState] = useState(false)
  const setHasValidationErrors = useCallback((val: boolean) => {
    setHasValidationErrorsState(val)
  }, [])
  const { userEnterprise } = getAppBarState().appState

  // ─── Shared data state ───
  const [loadingSharedData, setLoadingSharedData] = useState(true)
  const [sharedDataError, setSharedDataError] = useState<string | null>(null)
  const [screens, setScreensState] = useState<ScreenDetail[]>([])
  const [assignments, setAssignmentsState] = useState<Assignment[]>([])
  const [enterprise, setEnterprise] = useState<EnterpriseInfo | null>(null)

  // Use a ref for save handlers to avoid re-render loops
  const saveHandlers = useRef<Record<string, () => Promise<void>>>({})

  // ─── Load shared data once ───
  const reloadSharedData = useCallback(async () => {
    setLoadingSharedData(true)
    setSharedDataError(null)
    try {
      const response = await fetchScreenDisplayDetail(userEnterprise.id)
      const sortedScreens = (response.data.screens || []).sort((a, b) =>
        (a.name || '').localeCompare(b.name || '', undefined, { numeric: true, sensitivity: 'base' }),
      )
      setScreensState(sortedScreens)

      if (response.data.assignments) {
        const sorted = response.data.assignments.sort((a, b) => a.order - b.order)
        setAssignmentsState(sorted)
      }

      setEnterprise(response.data.enterprise || null)
    } catch (err: any) {
      setSharedDataError(err.message || 'Error loading screen data')
    } finally {
      setLoadingSharedData(false)
    }
  }, [userEnterprise.id])

  useEffect(() => {
    reloadSharedData()
  }, [reloadSharedData])

  // ─── Setters for tabs that need to update shared state ───
  const setScreens = useCallback((newScreens: ScreenDetail[]) => {
    setScreensState(newScreens)
  }, [])

  const setAssignments = useCallback(
    (updater: Assignment[] | ((prev: Assignment[]) => Assignment[])) => {
      if (typeof updater === 'function') {
        setAssignmentsState(updater)
      } else {
        setAssignmentsState(updater)
      }
    },
    [],
  )

  // ─── Tab dirty / save logic ───
  const setTabDirty = useCallback((tabKey: string, isDirty: boolean) => {
    setDirtyTabs((prev) => {
      if (prev[tabKey] === isDirty) return prev
      return { ...prev, [tabKey]: isDirty }
    })
  }, [])

  const isTabDirty = useCallback(
    (tabKey: string) => !!dirtyTabs[tabKey],
    [dirtyTabs],
  )

  const registerSaveHandler = useCallback((tabKey: string, handler: (() => Promise<void>) | null) => {
    if (handler) {
      saveHandlers.current[tabKey] = handler
    } else {
      delete saveHandlers.current[tabKey]
    }
  }, [])

  const hasChanges = Object.values(dirtyTabs).some(Boolean)

  const onSave = useCallback(async () => {
    setSaving(true)
    try {
      // Execute save handlers only for dirty tabs
      const promises: Promise<void>[] = []
      for (const [tabKey, handler] of Object.entries(saveHandlers.current)) {
        if (dirtyTabs[tabKey]) {
          promises.push(handler())
        }
      }
      await Promise.all(promises)
    } finally {
      setSaving(false)
    }
  }, [dirtyTabs])

  const contextValue = useMemo(() => ({
    hasChanges,
    hasValidationErrors,
    saving,
    loadingSharedData,
    sharedDataError,
    screens,
    assignments,
    enterprise,
    reloadSharedData,
    setTabDirty,
    isTabDirty,
    setHasValidationErrors,
    registerSaveHandler,
    onSave,
    setScreens,
    setAssignments,
  }), [
    hasChanges,
    hasValidationErrors,
    saving,
    loadingSharedData,
    sharedDataError,
    screens,
    assignments,
    enterprise,
    reloadSharedData,
    setTabDirty,
    isTabDirty,
    setHasValidationErrors,
    registerSaveHandler,
    onSave,
    setScreens,
    setAssignments,
  ])

  return (
    <ScreensContext.Provider value={contextValue}>
      {children}
    </ScreensContext.Provider>
  )
}

export const useScreensContext = (): ScreensContextValue => {
  const ctx = useContext(ScreensContext)
  if (!ctx) throw new Error('useScreensContext must be used inside <ScreensProvider>')
  return ctx
}
